/*
 * PaperClip custom storefront JS.
 *
 * Two unrelated features live in this file. Keep them that way — they are not connected, and
 * breaking one while editing the other is the most likely way to take down a working feature:
 *
 *   1. Webinar certificate claim  — /pages/certificate (see templates/page.certificate.liquid)
 *   2. Handouts password gate     — /pages/handouts (see sections/password-access.liquid)
 *
 * TICK-3339 (2026-08): rewritten from the previous single-line build. Changes:
 *   - The certificate UI moved from a sitewide modal to its own page, so the modal open/close
 *     handlers are gone. Everything else about the certificate flow behaves as before.
 *   - Webinar sorting now uses the `data-ts` unix timestamp rendered by Liquid instead of
 *     parsing "October 13, 2026" with `new Date()`. That parse succeeded in Chrome and returned
 *     NaN in Firefox and Safari, which collapsed the comparison and left webinars out of order —
 *     the bug Kelsi reported on 2025-03-28.
 *   - The handouts password gate below is unchanged in behaviour. Do not "tidy" it; see the note.
 */
(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // 1. Webinar certificate claim
  // ---------------------------------------------------------------------------

  var $select = $('#product-title');

  if ($select.length) {
    // Liquid renders data-ts as a unix timestamp. Anything unparseable sorts to the bottom
    // rather than corrupting the comparison.
    var timestampOf = function (el) {
      var ts = parseInt($(el).attr('data-ts'), 10);
      return isNaN(ts) ? 0 : ts;
    };

    var nowTs = Math.floor(Date.now() / 1000);
    var options = $select.find('option').get();

    options.sort(function (a, b) {
      return timestampOf(b) - timestampOf(a);
    });

    $select.empty();

    // Certificates are only claimable for webinars that have already taken place.
    options.forEach(function (option) {
      var ts = timestampOf(option);
      if (ts > 0 && ts < nowTs) {
        $select.append(option);
      }
    });

    $select.val($select.find('option:first').val());
  }

  var showCertificateError = function (message) {
    $('.alert__error-item').html(message);
    $('.alert.alert--error').removeClass('hideMe');
  };

  // Replace each SVG <img> inside `el` with a 4x PNG rendered at the image's current laid-out
  // size. Idempotent: images already converted are skipped. Resolves once all are swapped.
  function rasterizeCertificateSvgs(el) {
    var imgs = [].slice.call(el.querySelectorAll('img[src^="data:image/svg"]'));
    return Promise.all(imgs.map(function (img) {
      return new Promise(function (resolve) {
        var w = img.clientWidth || img.width;
        var h = img.clientHeight || img.height;
        if (!w || !h) { resolve(); return; }
        var canvas = document.createElement('canvas');
        canvas.width = w * 4;
        canvas.height = h * 4;
        var src = new Image();
        src.onload = function () {
          canvas.getContext('2d').drawImage(src, 0, 0, canvas.width, canvas.height);
          img.src = canvas.toDataURL('image/png');
          resolve();
        };
        src.onerror = function () { resolve(); };
        src.src = img.src;
      });
    }));
  }

  $('.form__submit__certificate').on('click', function (event) {
    event.preventDefault();

    $('.alert.alert--error').addClass('hideMe');

    var $selected = $('#product-title option:selected');

    if (!$selected.length) {
      showCertificateError('Please select a webinar.');
      return;
    }

    var enteredPassword = $('#password').val();
    var institution = $('#institution').val();
    var title = $('#title').val();

    if (!institution.length || !title.length || !enteredPassword.length) {
      showCertificateError('Please make sure all the fields are filled in.');
      return;
    }

    if (enteredPassword !== $selected.attr('code')) {
      showCertificateError("Password didn't match. Please check the password on your instructions sheet.");
      return;
    }

    $('#cert-container-pdf').removeClass('hideMe');
    $('#cert-container-pdf .webinaryName').html($select.val());
    $('#cert-container-pdf .webinarySubtitle').html($selected.attr('subtitle'));
    $('#cert-container-pdf .date').html($selected.attr('date'));
    $('#cert-container-pdf .institutionName').html(institution);

    // Tag the customer with their stated title and institution. Best-effort — a failure here
    // must not stop the certificate downloading.
    $.ajax({
      url: 'https://paper-clip-taggerf.gadget.app/tags',
      method: 'post',
      data: {
        id: $('#customer-id').val(),
        tags: 'title-' + title + ', institution-' + institution + ', certificate'
      },
      error: function (jqXHR, textStatus, errorThrown) {
        console.error('Error updating customer account:', errorThrown);
      }
    });

    // Render the fixed 800x613 certificate itself (#content2), not its full-width flex wrapper,
    // and centre it on the letter-landscape page with margins computed from its px size so the
    // canvas maps 1:1 onto a single page (the -0.05in keeps rounding from spilling a 2nd page).
    //
    // Amy, 2026-08-26 (follow-up to TICK-3783): the first cut clipped the certificate and pushed
    // it down the page. Two causes, both handled here:
    //   - html2canvas 1.x offsets the capture by the window scroll position unless scrollX/Y are
    //     pinned to 0 — the Download button sits below the fold, so every real render was scrolled.
    //   - Safari draws SVG <img>s at their intrinsic size inside html2canvas, ignoring CSS sizing.
    //     rasterizeCertificateSvgs() pre-renders each SVG to a 4x PNG at its laid-out size first,
    //     which every browser then scales correctly (and stays crisp at scale: 4).
    var certEl = document.getElementById('content2');
    var certW = 800, certH = 613;
    var marginX = (11 - certW / 96) / 2;
    var marginY = (8.5 - certH / 96) / 2 - 0.05;

    rasterizeCertificateSvgs(certEl).then(function () {
      return html2pdf(certEl, {
        margin: [marginY, marginX, marginY, marginX],
        filename: 'paperclip-certificate.pdf',
        image: { type: 'jpeg', quality: 1 },
        html2canvas: { scale: 4, letterRendering: true, scrollX: 0, scrollY: 0, width: certW, height: certH },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'landscape' }
      });
    }).then(function () {
      $('#cert-container-pdf').addClass('hideMe');
    });
  });

  // ---------------------------------------------------------------------------
  // 2. Handouts password gate  (/pages/handouts)
  // ---------------------------------------------------------------------------
  //
  // The password is stored as a cart attribute so sections/password-access.liquid can read it
  // server-side and match it against the `code:` tag on the webinar product.
  //
  // The clear-on-load below is deliberate and load-bearing. By the time it runs, Liquid has
  // already rendered the page using the attribute, so the customer sees their files — and the
  // attribute is then wiped, which is what makes access one page view long rather than sticky
  // for the rest of the session. Removing it silently turns a one-shot gate into a persistent
  // one.

  fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attributes: { password: '' } })
  }).catch(function (error) {
    console.error('Error clearing handout password attribute:', error);
  });

  var passwordButton = document.getElementById('password-protection');

  if (passwordButton) {
    passwordButton.addEventListener('click', function () {
      var password = document.getElementById('password-protection-input').value;

      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { password: password } })
      })
        .then(function (response) {
          return response.json();
        })
        .then(function () {
          window.location.reload();
        })
        .catch(function (error) {
          console.error('Error setting handout password attribute:', error);
        });
    });
  }
})();
